import React from 'react';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import type { ButtonProps } from '@mui/material/Button';
import type { SxProps, Theme } from '@mui/material/styles';

import {
  COLORS,
  FOCUS_RING,
  RADII,
  SHADOW,
  softTint,
} from '../../../theme/tokens';

export type GritButtonIntent =
  | 'primary'
  | 'secondary'
  | 'ghost'
  | 'danger'
  | 'warning';

export interface GritButtonProps
  extends Omit<ButtonProps, 'variant' | 'color' | 'startIcon' | 'endIcon'> {
  intent?: GritButtonIntent;
  loading?: boolean;
  leadingIcon?: React.ReactNode;
  trailingIcon?: React.ReactNode;
  sx?: SxProps<Theme>;
}

const intentStyles: Record<GritButtonIntent, SxProps<Theme>> = {
  primary: {
    'backgroundColor': COLORS.signalGreen,
    'color': COLORS.signalGreenInk,
    '&:hover': {
      backgroundColor: COLORS.signalGreenHover,
      boxShadow: SHADOW.hoverLift,
    },
  },
  secondary: {
    'backgroundColor': COLORS.frameNavy,
    'color': COLORS.onFrame,
    '&:hover': {
      backgroundColor: COLORS.frameNavyTint,
      boxShadow: SHADOW.hoverLift,
    },
  },
  ghost: {
    'backgroundColor': 'transparent',
    'color': COLORS.navy,
    'border': `1px solid ${COLORS.borderSoft}`,
    '&:hover': {
      borderColor: COLORS.navy,
      backgroundColor: softTint(COLORS.navy, 0.08),
    },
  },
  danger: {
    'backgroundColor': COLORS.statusAlert,
    'color': COLORS.onAccent,
    '&:hover': {
      backgroundColor: COLORS.statusAlertInk,
      boxShadow: SHADOW.hoverLift,
    },
  },
  // The one place amber carries white text — the exception/warning convention
  // (design_handoff_modals: the destructive "Decommission device" pill).
  warning: {
    'backgroundColor': COLORS.warningAmber,
    'color': COLORS.onAccent,
    '&:hover': {
      backgroundColor: COLORS.warningAmberHover,
      boxShadow: SHADOW.hoverLift,
    },
  },
};

const GritButton = React.forwardRef<HTMLButtonElement, GritButtonProps>(
  (
    {
      children,
      disabled,
      intent = 'primary',
      loading = false,
      leadingIcon,
      trailingIcon,
      sx,
      type = 'button',
      ...buttonProps
    },
    ref
  ) => {
    const busy = loading || undefined;
    const spinner = (
      <CircularProgress
        aria-hidden
        size={16}
        thickness={5}
        sx={{ color: 'currentColor' }}
      />
    );

    return (
      <Button
        {...buttonProps}
        ref={ref}
        type={type}
        variant="contained"
        disableElevation
        disabled={disabled || loading}
        aria-busy={busy}
        startIcon={loading ? spinner : leadingIcon}
        endIcon={trailingIcon}
        sx={
          [
            {
              'minHeight': 36,
              'borderRadius': `${RADII.pill}px`,
              'px': 2,
              'fontWeight': 700,
              'letterSpacing': 0,
              'textTransform': 'none',
              'boxShadow': 'none',
              '&:focus-visible': FOCUS_RING,
            },
            intentStyles[intent],
            sx,
          ] as SxProps<Theme>
        }
      >
        {children}
      </Button>
    );
  }
);

GritButton.displayName = 'GritButton';

export default GritButton;
